import { pack } from "@adora-so/packer";
import { EventType, IncrementalSource } from "@adora-so/types";
import { createCustomCohortAddEvent, unreachable, } from "@adora/lib";
import { getAutoCohorts, retrieveBootstrapCohorts } from "./bootstrap-cohorts";
import { extractCustomCohortsFromQueryParams } from "./cohort-parsers/query-params";
import { debugLog } from "./debug";
import { redactURL } from "./redact";
import { urlAccepted } from "./url-accepted";
import { extractUTM } from "./utm";
import { SNIPPET_VERSION } from "./version";
// We use local/session storage to store events, so we need to limit the size of the events.
// 5mb is the max for these so we set it to 3mb to be safe.
const MAX_EVENT_BYTE_SIZE = 3 * 1024 * 1024;
// We want to limit how many requests we send when checking the event size because
// if there are lots of mutations (for example when using a lottie/animated svg)
// we can quickly reach the limit and spam the network. This throttle avoids that.
const MAX_SIZE_EVENT_SEND_THROTTLE = 5000; // 5 seconds
export class EventSync {
    window;
    storage;
    eventHolder;
    recorder;
    api;
    organization;
    windowGetters;
    lastSendTime = -1;
    lastAutoCohorts = undefined;
    fastpathToken = undefined;
    constructor(window, storage, eventHolder, recorder, api, organization, windowGetters) {
        this.window = window;
        this.storage = storage;
        this.eventHolder = eventHolder;
        this.recorder = recorder;
        this.api = api;
        this.organization = organization;
        this.windowGetters = windowGetters;
        recorder.setOnEvent((e) => this.onEvent(e));
    }
    onEvent(event) {
        // Full snapshots are often too large to store in session storage, so we have to try and eagerly save them.
        if (event.type === EventType.FullSnapshot) {
            let cohortAddEvent;
            const bootstrappedCohorts = retrieveBootstrapCohorts(this.window, this.organization.cohortsBootstrapConfig);
            const utmEvents = extractUTM(this.windowGetters.getHref());
            const customCohortsFromQueryParams = extractCustomCohortsFromQueryParams(this.windowGetters.getHref(), this.organization.customQueryParamsCohorts);
            const autoCohorts = getAutoCohorts(this.window);
            this.lastAutoCohorts = autoCohorts;
            if (utmEvents ||
                bootstrappedCohorts ||
                autoCohorts ||
                customCohortsFromQueryParams) {
                cohortAddEvent = createCustomCohortAddEvent({
                    payload: {
                        userCohorts: {
                            ...(bootstrappedCohorts || {}),
                            ...(autoCohorts || {}),
                            ...(utmEvents || {}),
                            ...(customCohortsFromQueryParams || {}),
                        },
                        source: "window_getters",
                    },
                    delay: event.delay,
                    timestamp: event.timestamp,
                });
            }
            // Save it here before sending it to the event holder because the event holder does a calculation
            // to calculate the size of the events that we don't need to do
            const events = cohortAddEvent
                ? [...this.eventHolder.getEvents(), cohortAddEvent, event]
                : [...this.eventHolder.getEvents(), event];
            this.clearAndSaveEvents(events);
            return;
        }
        else if (event.type === EventType.IncrementalSnapshot &&
            event.data.source === IncrementalSource.Mutation) {
            // TODO: should this be bootstrapped cohorts too?
            const autoCohorts = getAutoCohorts(this.window);
            if (autoCohorts &&
                (!this.lastAutoCohorts ||
                    Object.keys(autoCohorts).length !==
                        Object.keys(this.lastAutoCohorts).length)) {
                debugLog("auto cohorts different, appending new event", autoCohorts);
                this.eventHolder.append(createCustomCohortAddEvent({
                    payload: {
                        userCohorts: autoCohorts,
                        source: "window_getters",
                    },
                    delay: event.delay,
                    timestamp: event.timestamp,
                }));
            }
            this.lastAutoCohorts = autoCohorts;
        }
        this.eventHolder.append(event);
        const serializedEventsSize = this.eventHolder.estimatedSerializedByteSize;
        const currentTime = Date.now();
        const timeSinceLastSend = currentTime - this.lastSendTime;
        if (serializedEventsSize > MAX_EVENT_BYTE_SIZE &&
            timeSinceLastSend >= MAX_SIZE_EVENT_SEND_THROTTLE) {
            debugLog(`MAX_EVENT_BYTE_SIZE and timeSinceLastSend exceeded: ${serializedEventsSize} > ${MAX_EVENT_BYTE_SIZE} and ${timeSinceLastSend} >= ${MAX_SIZE_EVENT_SEND_THROTTLE}`);
            this.clearAndSaveEvents();
        }
    }
    async clearAndSaveEvents(_events) {
        const eventHref = this.windowGetters.getHref();
        // This isn't quite right, we'll potentially be losing events that were from accepted urls if they're in the event holder.
        // This is just a quick fix because there may be a quick url change to a blocked url and a full snapshot just before stopping.
        if (!urlAccepted({
            urlStr: eventHref,
            pathConfig: this.organization.pathConfig,
        })) {
            return;
        }
        const events = _events || this.eventHolder.getEvents();
        if (events.length === 0) {
            return;
        }
        this.eventHolder.clear();
        try {
            debugLog("saving events...");
            const sendResult = await this.prepareAndSendEvents(events);
            debugLog("save events result", sendResult.type);
            switch (sendResult.type) {
                case "success":
                    break;
                case "recreate-session":
                    await this.recreateSession(sendResult.session);
                    break;
                case "no-session":
                case "unknown-failure":
                case "rate-limit-exceeded":
                    this.storage.removeSession();
                    this.eventHolder.clear();
                    break;
                default:
                    unreachable(sendResult);
            }
        }
        catch (e) {
            debugLog("error saving events", e);
            // Destroy the sessions so it does not get re-used, and then re-throw the error
            // so the current session terminates.
            this.storage.removeSession();
            throw e;
        }
    }
    async prepareAndSendEvents(
    // Passing events in here rather than reading them from the event holder because this may be called
    // asynchronously meaning that the events in the event holder may have changed or been cleared.
    events) {
        this.lastSendTime = Date.now();
        let session = this.storage.getSession();
        if (!session) {
            return {
                type: "no-session",
            };
        }
        // Attempt to refresh the token earlier if needbe.
        if (session.eventPostUrl && session.eventPostToken) {
            const decoded = JSON.parse(atob(session.eventPostToken.split(".")[1]));
            if (decoded && Date.now() > decoded.lastEventAt - 10 * 1000) {
                debugLog("refreshing token as it is about to expire");
                const result = await this.refreshToken(session);
                if (result.type !== "success") {
                    return result;
                }
                session = this.storage.getSession();
                if (!session) {
                    return {
                        type: "no-session",
                    };
                }
            }
        }
        const packedEvents = session.compressionType === "RRWEB_PACK"
            ? events.map((event) => pack(event))
            : events;
        const saveSessionEventsResponse = await this.api.saveSessionEvents({
            sessionId: session.id,
            events: packedEvents,
            metadata: {
                url: redactURL(this.windowGetters.getHref(), this.organization.allowedQueryParams, this.organization.pathConfig),
            },
            eventPostUrl: session.eventPostUrl,
            eventPostToken: session.eventPostToken,
            fastpathToken: this.fastpathToken,
        });
        if (!saveSessionEventsResponse) {
            return { type: "unknown-failure" };
        }
        if (saveSessionEventsResponse.error) {
            switch (saveSessionEventsResponse.error.code) {
                case "SessionNotFound": // Session not found
                case "SessionAlreadySynced": // Session already synced
                    return {
                        type: "recreate-session",
                        session,
                    };
                case "RateLimitExceeded": // Rate limit exceeded
                    return { type: "rate-limit-exceeded" };
            }
        }
        if (!saveSessionEventsResponse.data) {
            return { type: "unknown-failure" };
        }
        if (saveSessionEventsResponse.data &&
            "code" in saveSessionEventsResponse.data) {
            switch (saveSessionEventsResponse.data.code) {
                case "TOKEN_REFRESH": {
                    debugLog("refreshing token as it expired on the worker");
                    const result = await this.refreshToken(session);
                    if (result.type !== "success") {
                        return result;
                    }
                    return this.prepareAndSendEvents(events);
                }
                default:
                    unreachable(saveSessionEventsResponse.data.code);
            }
        }
        this.fastpathToken = saveSessionEventsResponse.data.fastpathToken;
        if (saveSessionEventsResponse.response.status < 200 ||
            saveSessionEventsResponse.response.status > 201) {
            throw new Error(`got status ${saveSessionEventsResponse.response.status}`);
        }
        return { type: "success" };
    }
    async refreshToken(session) {
        // Try again.
        const refreshTokenResponse = await this.api.refreshEventToken({
            sessionId: session.id,
        });
        if (!refreshTokenResponse) {
            return { type: "unknown-failure" };
        }
        if (refreshTokenResponse.error) {
            switch (refreshTokenResponse.error.code) {
                case "SessionNotFound": // Session not found
                case "SessionAlreadySynced": // Session already synced
                    return {
                        type: "recreate-session",
                        session,
                    };
                case "RateLimitExceeded": // Rate limit exceeded
                    return { type: "rate-limit-exceeded" };
            }
        }
        if (!refreshTokenResponse.data) {
            return { type: "unknown-failure" };
        }
        this.storage.setSession({
            ...session,
            eventPostToken: refreshTokenResponse.data.token,
        });
        return { type: "success" };
    }
    async recreateSession(session) {
        debugLog("recreating sessions");
        let startedSession;
        const wasRecording = this.recorder.isRecording;
        // Stop the recording because the session has stopped, will be started if the session is successfully recreated
        this.recorder.setRecording(false);
        this.storage.removeSession();
        this.fastpathToken = undefined;
        let eventPostUrl;
        let eventPostToken;
        try {
            debugLog("recreating session...");
            // TODO: share implementation with `startSession`.
            const startSessionResponse = await this.api.startSession({
                orgId: session.settings.orgId,
                language: session.settings.language || this.windowGetters.getLanguage(),
                domain: new URL(this.windowGetters.getHref()).hostname,
                userCohorts: session.settings.userCohorts || {},
                uid: session.settings.uid,
                devicePixelRatio: this.windowGetters.getDevicePixelRatio(),
                adoraUserId: session.adoraUserId,
                documentReferrer: session.documentReferrer,
                snippetVersion: SNIPPET_VERSION,
            });
            if (!startSessionResponse) {
                return;
            }
            if (startSessionResponse.error) {
                return;
            }
            if (startSessionResponse.response.status !== 201) {
                debugLog("session not created, aborting");
                return;
            }
            if (!startSessionResponse.data ||
                !("session" in startSessionResponse.data)) {
                debugLog("malformed session, exiting");
                return;
            }
            if (startSessionResponse?.data &&
                "eventPostUrl" in startSessionResponse.data &&
                startSessionResponse.data.eventPostUrl &&
                "eventPostToken" in startSessionResponse.data &&
                startSessionResponse.data.eventPostToken) {
                eventPostUrl = startSessionResponse.data.eventPostUrl;
                eventPostToken = startSessionResponse.data.eventPostToken;
            }
            startedSession = startSessionResponse.data;
        }
        catch (_e) {
            // Do nothing
        }
        if (!startedSession) {
            return;
        }
        const newSessionData = {
            id: startedSession.session.id,
            createdAt: startedSession.session.createdAt,
            status: startedSession.session.status,
            lastSyncedAt: startedSession.session.lastSyncedAt,
            domain: startedSession.session.domain,
            requestIp: startedSession.session.requestIp,
            country: startedSession.session.country,
            userAgent: startedSession.session.userAgent,
            language: startedSession.session.language,
            initialUserCohorts: startedSession.session.initialUserCohorts,
            devicePixelRatio: startedSession.session.devicePixelRatio,
            organization: startedSession.organization,
            settings: session.settings,
            adoraUserId: session.adoraUserId,
            documentReferrer: session.documentReferrer,
            canvasRecordingEnabled: session.canvasRecordingEnabled,
            eventPostUrl,
            eventPostToken,
            limitedCanvasRecording: session.limitedCanvasRecording,
            compressionType: session.compressionType,
        };
        this.storage.setSession(newSessionData);
        // Clear events because we're about to start a new recording and the events can't just be added to the new session,
        // prepended to the new recording
        this.eventHolder.clear();
        // Restart the session recording so that we get a full snapshot for the new session.
        if (wasRecording) {
            this.recorder.setRecording(true);
        }
    }
}
